import React, { useCallback, useState, type SyntheticEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { ModalTransition } from '@atlaskit/modal-dialog';
import {
	DiscardSuggestionsAllFromGlobalCreateModal,
	DiscardSuggestionsCurrentSuggested,
	DiscardSuggestionsCurrentEdited,
} from './main';

export { useShouldShowConfirmDismissAiSuggestedModal } from './use-should-show-confirm-dismiss-ai-suggested-modal';

export const DISCARD_MODAL_TYPE = {
	// DISCARD_ALL modal is part of ai-issue-breakdown/ui/issue-breakdown-draft-list/discard-suggestions
	DISCARD_ALL_FROM_GLOBAL_CREATE: 'DISCARD_ALL_FROM_GLOBAL_CREATE',
	DISCARD_CURRENT_SUGGESTED: 'DISCARD_CURRENT_SUGGESTED',
	DISCARD_CURRENT_EDITED: 'DISCARD_CURRENT_EDITED',
} as const;

export type ConfirmDismissAiSuggestedModal =
	(typeof DISCARD_MODAL_TYPE)[keyof typeof DISCARD_MODAL_TYPE];

export type ModalButtonAction = (
	_: SyntheticEvent,
	analyticsEvent: UIAnalyticsEvent,
	type: ConfirmDismissAiSuggestedModal,
) => void;

export type Props = {
	onDiscard: ModalButtonAction;
	onClose: () => void;
	modalType?: ConfirmDismissAiSuggestedModal;
};

export const DiscardSuggestionsModal = ({ modalType, onDiscard, onClose }: Props) => {
	const onDiscardClick = useCallback(
		(_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			modalType && onDiscard(_, analyticsEvent, modalType);
		},
		[onDiscard, modalType],
	);

	const renderDiscardModal = useCallback(() => {
		switch (modalType) {
			case DISCARD_MODAL_TYPE.DISCARD_ALL_FROM_GLOBAL_CREATE:
				return (
					<DiscardSuggestionsAllFromGlobalCreateModal
						onClose={onClose}
						onContinue={onDiscardClick}
					/>
				);
			case DISCARD_MODAL_TYPE.DISCARD_CURRENT_SUGGESTED:
				return <DiscardSuggestionsCurrentSuggested onClose={onClose} onContinue={onDiscardClick} />;
			case DISCARD_MODAL_TYPE.DISCARD_CURRENT_EDITED:
				return <DiscardSuggestionsCurrentEdited onClose={onClose} onContinue={onDiscardClick} />;
			default:
				return null;
		}
	}, [modalType, onClose, onDiscardClick]);

	return <ModalTransition>{modalType && renderDiscardModal()}</ModalTransition>;
};

type UseConfirmDismissAiSuggestedModalType = [
	ConfirmDismissAiSuggestedModal | undefined,
	(modalType: ConfirmDismissAiSuggestedModal | undefined) => void,
];

export const useConfirmDismissAiSuggestedModal = (): UseConfirmDismissAiSuggestedModalType => {
	const [visibleModal, setVisibleModal] = useState<ConfirmDismissAiSuggestedModal | undefined>();
	const setVisibleModalTo = useCallback(
		(modalType: ConfirmDismissAiSuggestedModal | undefined) => {
			setVisibleModal(modalType);
		},
		[setVisibleModal],
	);

	return [visibleModal, setVisibleModalTo];
};
